import {shareReplay, distinctUntilChanged, first, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {DialogConfiguration} from '../cache/uiconfiguration/dialogconfiguration';
import {SupportedLocaleConfiguration} from '../cache/uiconfiguration/supportedlocaleconfiguration';
import {Observable} from 'rxjs';

@Injectable()
export class LanguageService {
  private langCode: Observable<string> = null;

  public static getBrowserLanguage(): string {
    // Check for modern browsers first (languages array)
    if (window.navigator.languages && window.navigator.languages.length > 0) {
      return window.navigator.languages[0];
    }
    // Fallback for older browsers
    return (window.navigator as any).userLanguage || window.navigator.language;
  }

  public getLanguage(
    supportedLocales: Observable<SupportedLocaleConfiguration>,
  ): Observable<string> {
    if (this.langCode == null) {
      // Format must be en-US, de-DE
      const langCode = LanguageService.getBrowserLanguage();
      this.langCode = supportedLocales.pipe(
        map((locConfig: SupportedLocaleConfiguration) => locConfig.supportedlanguagecode),
        first(),
        map((localeList: string[]) => {
          let match = 'en-US';
          localeList.forEach((locale: string) => {
            if (locale.substr(0, 2).toLowerCase() === langCode.substr(0, 2).toLowerCase()) {
              match = locale;
            }
          });
          return match;
        }),
        distinctUntilChanged(),
        shareReplay(1),
      );
    }
    return this.langCode;
  }

  public getLanguageShort(
    supportedLocales: Observable<SupportedLocaleConfiguration>,
  ): Observable<string> {
    if (this.langCode == null) {
      // Format must be en-US, de-DE
      const langCode: string = LanguageService.getBrowserLanguage();
      this.langCode = supportedLocales.pipe(
        map((locConfig: SupportedLocaleConfiguration) => locConfig.supportedlanguagecode),
        first(),
        map((localeList: string[]) => {
          let match = 'en-US';
          localeList.forEach((locale: string) => {
            if (locale.substr(0, 2).toLowerCase() === langCode.substr(0, 2).toLowerCase()) {
              match = locale;
            }
          });
          return match.substring(0, 2);
        }),
        distinctUntilChanged(),
        shareReplay(1),
      );
    }
    return this.langCode;
  }

  public static resolveDialogSymbol(name: string, dgConf: DialogConfiguration): string {
    if (dgConf == null) return;
    return LanguageService.resolveSymbol(name, dgConf.stringresources);
  }

  private static resolveSymbol(name: string, stringresources: string): string {
    const stringResourceRegex: RegExp =
      /\<SymbolResourcePair Symbol="([^"]+)" ResourceString="([^"]+)"\s*\/>/g;
    if (stringresources == null) {
      return null;
    }
    let matches: RegExpExecArray;
    while ((matches = stringResourceRegex.exec(stringresources))) {
      if (matches[1] == name) {
        return matches[2];
      }
    }
    return null;
  }

  // Use this method for translating static text values
  public static translate(
    name: string,
    defaultValue: string,
    stringresources: string,
    args?: string[],
  ): string {
    let value: string;
    if (LanguageService.resolveSymbol(name, stringresources) == null) {
      value = defaultValue;
    } else {
      value = LanguageService.resolveSymbol(name, stringresources);
    }

    if (args != null && Array.isArray(args) && args.length > 0) {
      value = value.replace(/{(\d+)}/g, (match: string, num: number) => {
        return typeof args[num] != 'undefined' ? args[num] : match;
      });
    }

    return value;
  }

  public static getErrorMessage(errorCode: string, languageResource: string): string {
    if (errorCode == null) return '';
    let languageKey: string = errorCode
      .split('-')
      .map(s => s.charAt(0).toLocaleUpperCase() + s.substr(1))
      .join('');
    let defaultValue: string;
    switch (errorCode) {
      case 'permission-denied':
        defaultValue = 'Permission denied.';
        break;
      case 'resource-not-found':
        defaultValue = 'The specified resource was not found.';
        break;
      case 'bad-request':
        defaultValue = 'The request was not valid.';
        break;
      case 'resource-management-exception':
        defaultValue = 'The request caused an exception in the resource management service.';
        break;
      case 'internal-server-error':
      case 'interal-server-error':
        defaultValue = 'A internal server error occurred.';
        break;
      case 'server-error':
        defaultValue = 'The server could not be reached. Please check your internet connection.';
        break;
      case 'unauthorized':
        defaultValue = 'You are not authorized. Please sign in again.';
        break;
      case 'gateway-timeout':
        defaultValue = 'The server took too long to answer.';
        break;
      case 'not-supported':
        defaultValue = 'This operation is not available on this backend.';
        break;
      default:
        defaultValue = languageKey;
        break;
    }

    return LanguageService.translate(
      'WebserviceError' + languageKey,
      defaultValue,
      languageResource,
    );
  }
}
